import { useState, useEffect } from 'react';
import { getOptimizedAvatarUrl } from '../utils/cloudinary';
import { usePersona } from '../contexts/PersonaContext';

/**
 * Hook to fetch and return the current logged-in user's avatar URL.
 * - Creators: uses profile.avatar_url
 * - Hosts: uses profile.logo_url, falls back to first property image
 * - Travelers: no avatar (returns null)
 *
 * Re-fetches whenever the active persona changes so dual-persona users see the
 * avatar for the persona they're currently using.
 *
 * @returns {{ avatarUrl: string | null, loading: boolean }}
 */
export function useCurrentUserAvatar() {
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const { activePersona } = usePersona();

  useEffect(() => {
    let cancelled = false;

    const fetchAvatar = async () => {
      setLoading(true);
      setAvatarUrl(null);
      try {
        const email = typeof window !== 'undefined'
          ? window.localStorage.getItem('postaway_email')
          : null;
        const userType = activePersona || (typeof window !== 'undefined'
          ? window.localStorage.getItem('postaway_user_type')
          : null);

        if (!email || !userType) return;
        if (userType === 'traveler') return;

        const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost'
          ? 'http://localhost:4000'
          : '';

        let profileUrl = null;
        if (userType === 'creator') {
          profileUrl = `${apiBase}/api/creator/profile?email=${encodeURIComponent(email)}`;
        } else if (userType === 'host') {
          profileUrl = `${apiBase}/api/host/profile?email=${encodeURIComponent(email)}`;
        } else if (userType === 'partner') {
          profileUrl = `${apiBase}/api/partner/profile?email=${encodeURIComponent(email)}`;
        }

        if (!profileUrl) return;

        const response = await fetch(profileUrl);
        const data = await response.json();
        if (cancelled) return;

        if (response.ok && data.ok && data.profile) {
          let rawUrl = null;

          if (userType === 'creator') {
            rawUrl = data.profile.avatar_url || null;
          } else if (userType === 'host') {
            rawUrl = data.profile.logo_url || null;
            if (!rawUrl && data.property) {
              rawUrl = data.property.main_image_url || null;
            }
          } else if (userType === 'partner') {
            rawUrl = data.profile.logo_url || null;
          }

          if (rawUrl) {
            const optimized = getOptimizedAvatarUrl(rawUrl);
            if (!cancelled) setAvatarUrl(optimized || rawUrl);
          }
        }
      } catch (error) {
        if (!cancelled) console.warn('Failed to fetch user avatar:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAvatar();

    return () => {
      cancelled = true;
    };
  }, [activePersona]);

  return { avatarUrl, loading };
}

export default useCurrentUserAvatar;
